// API Endpoints
export const AUTH_ENDPOINTS = {
    LOGIN: '/auth/login',
    REGISTER: '/v1/users/',
    REQUEST_OTP: '/v1/auth/request-otp',
    VERIFY_OTP: '/v1/auth/verify-otp',
    REFRESH_TOKEN: '/v1/auth/refresh',
    GET_CURRENT_USER: '/v1/auth/me',
    GET_CONSENT: (phoneNumber: string) => `/v1/sms/status/${phoneNumber}`,

} as const;

export const USER_ENDPOINTS = {
    GET_CURRENT_USER: '/v1/auth/me',
    LIST_USERS: (skip = 0, limit = 10, storeId?: string | null) => {
        const params = new URLSearchParams({
            skip: skip.toString(),
            limit: limit.toString(),
        });
        if (storeId) {
            params.append('store_id', storeId);
        }
        return `/v1/users/?${params.toString()}`;
    },
    CREATE_USER: '/v1/users/',
    UPDATE_USER: (id: string | number) => `/v1/users/${id}`,
    LIST_STORE_USERS: (storeId: string) => `/v1/stores/${storeId}/users`,
    INVITE_USER_TO_STORE: (storeId: string) => `/v1/stores/${storeId}/invite`,
    EDIT_USER_IN_STORE: (storeId: string, userId: string) => `/v1/stores/${storeId}/users/${userId}`,
    SET_CASHIER_MODE_PIN: (storeId: string, userId: string) => `/v1/stores/${storeId}/users/${userId}/cashier-mode-pin`,
    DELETE_STORE_USER: (storeId: string, userId: string) => `/v1/stores/${storeId}/users/${userId}`,
    DELETE_USER: (userId: string) => `/v1/users/${userId}`,
} as const;

export const AUDIT_CODE_ENDPOINTS = {
    LIST_AUDIT_CODES: (storeId: string) => `/v1/stores/${storeId}/audit-codes`,
    GENERATE_AUDIT_CODE: (storeId: string, locationId: string) => `/v1/stores/${storeId}/locations/${locationId}/audit-code`,
    REVOKE_AUDIT_CODE: (storeId: string, locationId: string) => `/v1/stores/${storeId}/locations/${locationId}/audit-code`,
} as const;

export const HELP_GUIDE_ENDPOINTS = {
    LIST_MY_GUIDES: '/v1/help-guides/me',
    GET_MY_GUIDE: (guideRef: string) => `/v1/help-guides/me/${guideRef}`,
    UPDATE_MY_PROGRESS: (guideRef: string) => `/v1/help-guides/me/${guideRef}/progress`,
    LIST_SHARE_USERS: (guideRef: string, storeId: string) => `/v1/help-guides/me/${guideRef}/share/stores/${storeId}/users`,
    SHARE_GUIDE: (guideRef: string) => `/v1/help-guides/me/${guideRef}/share`,
} as const;

export const STORES_ENDPOINTS = {
    GET_STORES_SUMMARY: (startDate: string, endDate: string) =>
        `/v1/stores/audit-reports/user-summary?start_date=${startDate}&end_date=${endDate}`,
    GET_STORES_RECONCILIATION_SUMMARY: (startDate: string, endDate: string) =>
        `/v1/stores/audit-reports/reconciliation-summary?start_date=${startDate}&end_date=${endDate}`,
    GET_STORES_SUMMARY_STORES_ONLY: (startDate: string, endDate: string) =>
        `/v1/stores/audit-reports/user-summary/stores-only?start_date=${startDate}&end_date=${endDate}`,
    GET_STORE_RECONCILIATION_INVESTIGATION: (storeId: string) =>
        `/v1/stores/audit-reports/investigation/stores/${storeId}`,
    GET_EXPIRED_STORES: '/v1/stores/expired',
} as const;

export const RECONCILIATION_ENDPOINTS = {
    CREATE_STORE_RECONCILIATION: (storeId: string) => `/v1/audit-reconciliation/stores/${storeId}/reconciliations`,

    LIST_STORE_RECONCILIATIONS: (storeId: string) => `/v1/audit-reconciliation/stores/${storeId}/reconciliations`,

    UPDATE_RECONCILIATION: (reconciliationId: string) => `/v1/audit-reconciliation/reconciliations/${reconciliationId}`,

    STORE_RECONCILIATION_WITH_DATA: (storeId: string, reconciliationDate: string) =>
        `/v1/audit-reconciliation/stores/${storeId}/reconciliations/${reconciliationDate}`,

    GET_RECONCILIATION: (reconciliationId: string) => `/v1/audit-reconciliation/reconciliations/${reconciliationId}`,

    LIST_RECONCILIATIONS: () => `/v1/audit-reconciliation/reconciliations`,

    DELETE_RECONCILIATION: (reconciliationId: string) => `/v1/audit-reconciliation/reconciliations/${reconciliationId}`,

    UPSERT_RECONCILIATION: (storeId: string) => `/v1/audit-reconciliation/stores/${storeId}/reconciliations/upsert`,

    ADD_MANAGER_NOTE: (storeId: string) => `/v1/audit-reconciliation/stores/${storeId}/reconciliations/manager-note`,

    VERIFY_DAILY_RECONCILIATION: (storeId: string) => `/v1/audit-reconciliation/stores/${storeId}/reconciliations/verify`,

    ADD_VERIFICATION_NOTE: (storeId: string) => `/v1/audit-reconciliation/stores/${storeId}/reconciliations/verification-note`,

    RESOLVE_DISCREPANCIES: (reconciliationId: string) =>
        `/v1/audit-reconciliation/reconciliations/${reconciliationId}/discrepancies`,
} as const;

export const AUDIT_ENDPOINTS = {
    GET_ACTIVATED_PACKS: (storeId: string) => `/v1/stores/${storeId}/activated-packs`,
    CREATE_PACK_LOADED_ACTIVITY: (storeId: string) => `/v1/stores/${storeId}/vending/activities/pack-loaded`,
    CREATE_PACK_REMOVED_ACTIVITY: (storeId: string) => `/v1/stores/${storeId}/vending/activities/pack-removed`,
    VALIDATE_VENDING_ACTIVITY_GAME: (storeId: string, gameId: string | number) => `/v1/stores/${storeId}/vending/activities/game/${gameId}`,
    GET_STORE_LOCATIONS: (storeId: string) => `/v1/stores/${storeId}/locations`,
    GET_LIVE_AUDIT_SCAN_STATUS: (storeId: string) => `/v1/audit-scans/stores/${storeId}/live-status`,
    VALIDATE_TICKET: '/v1/audits/validate',
    SUBMIT_STORE_AUDIT: (storeId: string) => `/v1/audits/${storeId}`,

    GET_STORE_AUDIT_SUMMARIES: (storeId: string, startDate: string, endDate: string) => `/v1/stores/audit-reports/date-range?store_id=${storeId}&start_date=${startDate}&end_date=${endDate}`,
    GET_STORE_RECONCILIATION_DAILY_SUMMARY_RANGE: (storeId: string, startDate: string, endDate: string) =>
        `/v1/stores/audit-reports/reconciliation-summary/stores/${storeId}/daily?start_date=${startDate}&end_date=${endDate}`,
    GET_STORE_RECONCILIATION_AUDITS_BY_DATE: (storeId: string, reportDate: string) =>
        `/v1/stores/audit-reports/reconciliation-summary/stores/${storeId}/audits?date=${reportDate}`,
    GET_STORE_SCRATCHER_RECONCILIATION_AUDITS_RANGE: (storeId: string, startDate: string, endDate: string) =>
        `/v1/stores/audit-reports/reconciliation-summary/stores/${storeId}/scratcher-audits?start_date=${startDate}&end_date=${endDate}`,
    GET_STORE_BACKSTOCK_LEDGER: (storeId: string, startDate: string, endDate: string) =>
        `/v1/stores/audit-reports/backstock-ledger/stores/${storeId}/daily?start_date=${startDate}&end_date=${endDate}`,
    GET_AUDIT_REPORT_PACK_SCOPE: (storeId: string) =>
        `/v1/stores/audit-reports/pack-scope/stores/${storeId}`,

    GET_AUDITS_BY_DATE: (storeId: string, date: string) => `/v1/audits/stores/${storeId}/audit-by-date?date=${date}`,

    GET_DAILY_EXPANDED_REPORT: (storeId: string, startDate: string, endDate?: string) => {
        const params = new URLSearchParams({
            store_id: storeId,
            start_date: startDate,
            end_date: endDate ?? startDate,
        });
        return `/v1/stores/audit-reports/daily/expanded?${params.toString()}`;
    },

    GET_STORE_MANUAL_DAY_ENTRY: (storeId: string, businessDate: string) => `/v1/stores/${storeId}/lottery/manual-day-entry?business_date=${businessDate}`,

    UPSERT_STORE_MANUAL_DAY_ENTRY: (storeId: string) => `/v1/stores/${storeId}/lottery/manual-day-entry`,

    UPSERT_STORE_POS_MANUAL_DAY_ENTRY: (storeId: string) => `/v1/stores/${storeId}/pos/manual-day-entry`,

    DELETE_STORE_MANUAL_DAY_ENTRY: (storeId: string, businessDate: string) => `/v1/stores/${storeId}/lottery/manual-day-entry?business_date=${businessDate}`,

    GET_AUDIT_DETAILS: (storeId: string, auditId: string) => `/v1/stores/audit-reports/${storeId}/audits/${auditId}/expanded`,

    GET_AUDIT_RECEIPT: (auditId: string) => `/v1/audits/${auditId}/receipt`,

    GET_AUDIT_RECEIPT_IMAGE: (id: string) => `v1/audits/receipt-audits/${id}/image`,

    GET_AUDIT_VIDEO: (auditId: string) => `/v1/audits/${auditId}/video`,

    GET_AUDIT_VIDEO_PLAYBACK_URL: (auditId: string) => `/v1/audits/${auditId}/video/playback-url`,

    GET_AUDITS_COMPLETE_THROUGH_DATE: (storeId: string) => `/v1/stores/${storeId}/audits-complete-through-date`,

    RESOLVE_DISCREPANCY: (storeId: string, auditId: string, discrepancyId: string) => `/v1/audits/${storeId}/discrepancies/${discrepancyId}/resolve`,

} as const;

export const SCRATCHER_ENDPOINTS = {
    GET_GAME_HISTORY: (storeId: string, gameId: string, status: string = 'all') =>
        `/v1/audits/stores/${storeId}/games/${gameId}/details?status=${status}`,

    GET_STORE_GAMES_LIST: (storeId: string) => `/v1/stores/${storeId}/games`,

    GET_GAME_PACK_REPORT: (storeId: string) => `/v1/stores/${storeId}/game-pack-report`,

    GET_CURRENT_INVENTORY_STATUS: (storeId: string) =>
        `/v1/stores/${storeId}/lottery/current-inventory-status`,

    GET_LOTTERY_PACKS: (storeId: string) =>
        `/v1/stores/${storeId}/lottery/packs`,

    GET_BACKSTOCK_AUDIT_STATUS: (storeId: string) =>
        `/v1/stores/${storeId}/lottery/packs/backstock-audits/overdue`,

    INVESTIGATE_PACK: (storeId: string, gameId: string, packId: string) =>
        `/v1/stores/${storeId}/lottery/packs/${gameId}/${packId}/investigate`,

    GET_GAME_PACK_DETAIL: (storeId: string, gameId: string) =>
        `/v1/stores/${storeId}/game-pack-report/${gameId}`,

    GET_PACK_LIFECYCLE: (storeId: string, gameId: string, packId: string) => `/v1/audits/stores/${storeId}/games/${gameId}/packs/${packId}/lifecycle`,

    GET_GAME_RECONCILIATION_LEDGER: (storeId: string, gameId: string) =>
        `/v1/stores/${storeId}/games/${gameId}/reconciliation-ledger`,

    UPDATE_PACK_LOCATION: (storeId: string, gameId: string, packId: string) =>
        `/v1/stores/${storeId}/games/${gameId}/packs/${packId}/location`,

    UPDATE_PRE_TRACKING_SOLD_OUT: (storeId: string, gameId: string, packId: string) =>
        `/v1/stores/${storeId}/games/${gameId}/packs/${packId}/pre-tracking-sold-out`,

    BULK_MANUAL_SOLD_OUT: (storeId: string) => `/v1/stores/${storeId}/packs/manual-sold-out`,

    GET_MANUAL_SOLD_OUT_REPORT: (storeId: string) => `/v1/stores/${storeId}/packs/manual-sold-out`,

    GET_MANUAL_SOLD_OUT_IMPACTS: (storeId: string) => `/v1/stores/${storeId}/packs/manual-sold-out/impacts`,

    MANUAL_SOLD_OUT_ELIGIBILITY: (storeId: string) => `/v1/stores/${storeId}/packs/manual-sold-out/eligibility`,
} as const;

export const CSTORE_ENDPOINTS = {
    GET_STORE_TRANSACTIONS: (
        storeId: string,
        date: string,
        category: string = 'all',
        filter: string,
        limit: number,
        pluCode?: string | null,
        tenderType?: string | null
    ) => {
        const params = new URLSearchParams({
            date,
            category,
            filter,
            limit: limit.toString(),
        });
        if (pluCode) {
            params.set('plu_code', pluCode);
        }
        if (tenderType && tenderType !== 'all') {
            params.set('tender_type', tenderType);
        }
        return `/v1/cstore/${storeId}/transactions?${params.toString()}`;
    },
    GET_STORE_TENDER_TYPES: (storeId: string, date: string) =>
        `/v1/cstore/${storeId}/transactions/tender-types?date=${encodeURIComponent(date)}`,
    GET_SCRATCHER_PLU_LIST: (storeId: string, date: string) =>
        `/v1/cstore/${storeId}/transactions/scratcher-plu-list?date=${encodeURIComponent(date)}`,
    GET_TRANSACTION_DETAILS: (
        storeId: string,
        transactionId: string,
        date: string,
        registerId?: string
    ) => {
        const params = new URLSearchParams({ date });
        if (registerId) {
            params.set('register_id', registerId);
        }
        return `/v1/cstore/${storeId}/transactions/${transactionId}?${params.toString()}`;
    },
    GET_STORE_CLOSE_REPORT: (storeId: string, fromDate: string, toDate: string) => {
        if (fromDate === toDate) {
            return `/v1/cstore/${storeId}/reports/store-close?date=${fromDate}`;
        }
        const params = new URLSearchParams({ from_date: fromDate, to_date: toDate });
        return `/v1/cstore/${storeId}/reports/store-close?${params.toString()}`;
    },
    GET_SALES_SUMMARY: (storeId: string, date: string, category: string = 'all', limit: number) =>
        `/v1/cstore/${storeId}/sales/summary?date=${date}&category=${category}&limit=${limit}`,
    GET_SALES_PERIODS: (storeId: string, date: string) =>
        `/v1/cstore/${storeId}/sales/periods?date=${encodeURIComponent(date)}`,
    GET_SHIFT_SALES_SUMMARY: (storeId: string, date: string, shift: string) => {
        const params = new URLSearchParams({ date, shift });
        return `/v1/cstore/${storeId}/sales/shift-summary?${params.toString()}`;
    },
    GET_STORE_DEPARTMENTS: (storeId: string, date: string) =>
        `/v1/cstore/${storeId}/departments?date=${date}`,
} as const;

export const PRICEBOOK_ENDPOINTS = {
    GET_STORE_ITEMS: (storeId: string) => `/v1/pricebook-queue/stores/${storeId}/items`,
    GET_MULTI_STORE_ITEMS: (storeId: string) => `/v1/pricebook-queue/stores/${storeId}/items/multi-store`,
    GET_STORE_ITEM_DETAIL: (storeId: string, scanCode: string) => `/v1/pricebook-queue/stores/${storeId}/items/${scanCode}`,
    CREATE_STORE_ITEM: (storeId: string) => `/v1/pricebook-queue/stores/${storeId}/items`,
    UPDATE_STORE_ITEM: (storeId: string, scanCode: string) => `/v1/pricebook-queue/stores/${storeId}/items/${scanCode}`,
    DELETE_STORE_ITEM: (storeId: string, scanCode: string) => `/v1/pricebook-queue/stores/${storeId}/items/${scanCode}`,
    EXPIRE_TRANSITION_ITEM: (storeId: string, scanCode: string) => `/v1/pricebook-queue/stores/${storeId}/items/${scanCode}/expire`,
    SEND_ITEMS_TO_POS: (storeId: string) => `/v1/pricebook-queue/stores/${storeId}/items/send-multi`,
    BULK_UPDATE_ITEMS: (storeId: string) => `/v1/pricebook-queue/stores/${storeId}/items/bulk-update`,
    GET_STORE_METADATA: (storeId: string) => `/v1/pricebook-queue/stores/${storeId}/metadata`,
    GET_INVENTORY_SUMMARY: (storeId: string) => `/v1/pricebook-queue/stores/${storeId}/inventory/summary`,
    GET_ITEM_SALES_SUMMARY: (storeId: string, scanCode: string) => `/v1/pricebook-queue/stores/${storeId}/items/${scanCode}/sales-summary`,
    GET_ITEM_SALES_SUMMARY_MONTHLY: (storeId: string, scanCode: string) => `/v1/pricebook-queue/stores/${storeId}/items/${scanCode}/sales-summary/monthly`,
    GET_ITEM_PRICE_COMPARISON: (scanCode: string) => `/v1/pricebook-queue/items/${scanCode}/price-comparison`,
    GET_ITEM_HISTORY: (storeId: string, scanCode: string) => `/v1/pricebook-queue/stores/${storeId}/items/${scanCode}/history`,
    GET_GAS_PRICE_GRADES: (storeId: string) => `/v1/pricebook-queue/fuel-prices/stores/${storeId}/grades`,
    UPDATE_GAS_PRICE_GRADE: (storeId: string, gradeId: string) => `/v1/pricebook-queue/fuel-prices/stores/${storeId}/grades/${gradeId}`,
    GET_ITEM_INVENTORY_SETTINGS: (storeId: string, scanCode: string) => `/v1/stores/${storeId}/items/${scanCode}/inventory/settings`,
    UPDATE_ITEM_INVENTORY_SETTINGS: (storeId: string, scanCode: string) => `/v1/stores/${storeId}/items/${scanCode}/inventory/settings`,
    UPDATE_ITEM_OPENING_BALANCE: (storeId: string, scanCode: string) => `/v1/stores/${storeId}/items/${scanCode}/inventory/opening-balance`,
    CREATE_ITEM_INVENTORY_ADJUSTMENT: (storeId: string, scanCode: string) => `/v1/stores/${storeId}/items/${scanCode}/inventory/adjustments`,
    GET_ITEM_INVENTORY_MOVEMENTS: (storeId: string, scanCode: string) => `/v1/stores/${storeId}/items/${scanCode}/inventory/movements`,
    CREATE_MANUAL_PURCHASE_LINE: (storeId: string) => `/v1/stores/${storeId}/finalized-purchase/manual-lines`,
    GET_FINALIZED_PURCHASE_LINES: (storeId: string) => `/v1/stores/${storeId}/finalized-purchase/lines`,
    GET_FINALIZED_PURCHASE_VENDORS: (storeId: string) => `/v1/stores/${storeId}/finalized-purchase/vendors/options`,
    CREATE_STORE_VENDOR: (storeId: string) => `/v1/stores/${storeId}/vendors`,
    CREATE_LINKED_CODE: '/v1/linked-codes/',
    GET_LINKED_CODES: (storeId: string) => `/v1/stores/${storeId}/linked-codes`,
    UPDATE_LINKED_CODE: (storeId: string, scanCode: string) => `/v1/stores/${storeId}/linked-codes/${scanCode}`,
    GET_PRICE_GROUPS: '/v1/price-groups',
    CREATE_PRICE_GROUP: '/v1/price-groups',
    GET_PRICE_GROUP_DETAIL: (priceGroupId: string) => `/v1/price-groups/${priceGroupId}`,
    UPDATE_PRICE_GROUP: (priceGroupId: string) => `/v1/price-groups/${priceGroupId}`,
    ASSIGN_PRICE_GROUP_ITEMS: (priceGroupId: string) => `/v1/price-groups/${priceGroupId}/items`,
} as const;

export const GAMES_ENDPOINTS = {
    GET_GAMES_POS_STATUS: (storeId: string) => `/v1/stores/${storeId}/games/pos-status`,
} as const;

export const ITEMS_ENDPOINTS = {
    GET_RELATED_SCAN_CODES: (upc: string, storeId: string, limit: number = 50) =>
        `/v1/items/related?upc=${encodeURIComponent(upc)}&store_id=${encodeURIComponent(storeId)}&limit=${limit}`,
} as const;
